import {t, Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {CreateFormInputDisplay} from '../../../../components/common/createFormInputs/createFormInputDisplay';
import {Dialog} from '../../../../components/common/dialogs/dialog';
import {DialogContent} from '../../../../components/common/dialogs/dialogContent';
import {DialogFooter} from '../../../../components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../../components/common/dialogs/dialogHeader';
import {useLinkNavigation} from '../../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {buildSettingsDevelopersPath} from '../../../../models/url/settingsUrlHelpers';
import {SettingsDevelopersPagesEnum, SettingsRootPagesEnum} from '../../../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../../../store/panels/panelStateBase';
import {createLocalApplicationForSettingsAsyncAction} from '../../../../store/panels/settings/settingsPanelActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../../banners/settingsBannersContext';

/*
 * Constants.
 */

const dialogWidth = 420; // px.

/*
 * Props.
 */

interface DevelopersApplicationsCreateDialogProps {
  panelId: SettingsPanelId;
  onRequestClose: Handler;
}

/*
 * Style.
 */

const StyledButtonGroup = styled(ButtonGroup)`
  flex: 1;
`;

const StyledDialogContent = styled(DialogContent)`
  margin-top: 0;
`;

/*
 * Component.
 */

export const DevelopersApplicationsCreateDialog: FC<DevelopersApplicationsCreateDialogProps> = (props) => {
  const {panelId, onRequestClose} = props;
  const localize = useLocalize();
  const {navigateToLink} = useLinkNavigation();
  const dispatch = useAppDispatch();

  const [applicationName, setApplicationName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreatingApplication, setIsCreatingApplication] = useState(false);
  const {showBanner} = useSettingsBanners();

  const onClose = () => {
    if (isCreatingApplication) {
      return;
    }
    onRequestClose();
  };

  const onCreateApplicationAsync = async () => {
    setIsCreatingApplication(true);
    const application = await dispatch(
      createLocalApplicationForSettingsAsyncAction(panelId, {name: applicationName, description}),
    );
    if (!application) {
      setIsCreatingApplication(false);
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: (
          <Trans id="developers-applications-create-dialog-failed-to-create-application">
            Failed to create application. Please try again.
          </Trans>
        ),
      });
      return;
    }
    navigateToLink(
      buildSettingsDevelopersPath({
        rootPage: SettingsRootPagesEnum.DEVELOPERS,
        subPage: SettingsDevelopersPagesEnum.APPLICATION_INFO,
        applicationId: application.id,
      }),
    );
    onRequestClose();
  };

  return (
    <Dialog width={dialogWidth} onRequestClose={onClose}>
      <DialogHeader title={<Trans id="developers-application-create-dialog-title">Create app</Trans>} />
      <StyledDialogContent>
        <CreateFormInputDisplay
          isRequired
          title={<Trans id="developers-application-create-dialog-app-name">App name</Trans>}
          input={
            <TextBox
              placeholder={localize(
                t('developers-application-create-dialog-app-name-placeholder')`Add app name`,
              )}
              value={applicationName}
              onChange={setApplicationName}
              isRequired
              noMargin
              shouldAutoFocus
              isDisabled={isCreatingApplication}
            />
          }
        />
        <CreateFormInputDisplay
          isRequired
          title={<Trans id="developers-application-create-dialog-app-description">Short description</Trans>}
          input={
            <TextBox
              placeholder={localize(
                t('developers-application-create-dialog-app-description-placeholder')`Add short description`,
              )}
              value={description}
              onChange={setDescription}
              isRequired
              noMargin
              isDisabled={isCreatingApplication}
            />
          }
        />
      </StyledDialogContent>
      <DialogFooter>
        <StyledButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onClose} isDisabled={isCreatingApplication}>
            <Trans id="developers-application-create-dialog-button-cancel">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={onCreateApplicationAsync}
            isDisabled={!applicationName.trim() || !description.trim() || isCreatingApplication}
          >
            <Trans id="developers-application-create-dialog-button-create">Create</Trans>
          </Button>
        </StyledButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};
